import React, { useEffect, useState } from "react";
import api from "../api";

export default function OutCallAutomation() {
  const [clients, setClients] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    client: "",
    campaign: "",
    to: "",
    cc: "",
    remarks: "",
  });

  const activeClientId = formData.client;

  /* ---------------- FETCH CLIENTS ---------------- */
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await api.get("/agents/clients-rights");

        // Sort alphabetically
        const sorted = res.data.sort((a, b) =>
          a.company_name.localeCompare(b.company_name)
        );

        setClients(sorted);

        // auto-select first client
        if (sorted.length > 0) {
          setFormData((p) => ({
            ...p,
            client: sorted[0].company_id,
          }));
        }
      } catch (err) {
        console.error("Client fetch failed", err);
        alert("Unable to load clients");
      }
    };

    fetchClients();
  }, []);

  /* ---------------- FETCH CAMPAIGNS ---------------- */
  useEffect(() => {
    if (!activeClientId) return;

    const fetchCampaigns = async () => {
      try {
        const res = await api.get(
          "/allocations/all-campaigns-with-company",
          {
            params: { company_id: activeClientId },
          }
        );
        setCampaigns(res.data || []);
      } catch (err) {
        console.error("Campaign fetch failed", err);
      }
    };

    fetchCampaigns();
  }, [activeClientId]);

  /* ---------------- FETCH SAVED RECORDS ---------------- */
  useEffect(() => {
    if (!activeClientId) return;

    const fetchRecords = async () => {
      try {
        const res = await api.get(
          "/allocations/list_outcall_automation",
          {
            params: { client: activeClientId },
          }
        );
        setRecords(res.data || []);
      } catch (err) {
        console.error("Records fetch failed", err);
      }
    };

    fetchRecords();
  }, [activeClientId]);

  /* ---------------- HANDLE CHANGE ---------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  /* ---------------- SAVE ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!activeClientId || !formData.campaign || !formData.to) {
      alert("Client, Campaign and To are required");
      return;
    }

    setLoading(true);
    try {
      await api.post("/allocations/save_outcall_automation", {
        company_id: activeClientId,
        campaign_name: formData.campaign,
        to: formData.to,
        cc: formData.cc,
        remarks: formData.remarks,
      });

      alert("✅ Saved successfully");

      setFormData((p) => ({
        ...p,
        campaign: "",
        to: "",
        cc: "",
        remarks: "",
      }));

      // refresh list
      const res = await api.get(
        "/allocations/list_outcall_automation",
        {
          params: { client: activeClientId },
        }
      );
      setRecords(res.data || []);
    } catch (err) {
      console.error(err);
      alert("❌ Failed to save");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- DELETE ---------------- */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this record?")) return;

    try {
      await api.delete(`/allocations/delete_outcall_automation/${id}`);
      setRecords((p) => p.filter((r) => r.id !== id));
    } catch (err) {
      console.error(err);
      alert("❌ Delete failed");
    }
  };

  return (
    <div className="mt-4">
      {/* FORM */}
      <div className="card shadow-sm mb-4">
        <div className="card-header fw-semibold">
          Out Call Report Automation
        </div>

        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              {/* Client */}
              <div className="col-md-4">
                <label className="form-label fw-semibold">Client</label>
                <select
                  name="client"
                  value={formData.client}
                  onChange={handleChange}
                  className="form-select"
                  required
                >
                  {clients.map((c) => (
                    <option key={c.company_id} value={c.company_id}>
                      {c.company_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Campaign */}
              <div className="col-md-4">
                <label className="form-label fw-semibold">Campaign</label>
                <select
                  name="campaign"
                  value={formData.campaign}
                  onChange={handleChange}
                  className="form-select"
                  required
                >
                  <option value="">Select Campaign</option>
                  {campaigns.map((c, i) => (
                    <option key={i} value={c.campaign}>
                      {c.campaign}
                    </option>
                  ))}
                </select>
              </div>

              {/* To */}
              <div className="col-md-4">
                <label className="form-label fw-semibold">To</label>
                <input
                  type="email"
                  name="to"
                  value={formData.to}
                  onChange={handleChange}
                  className="form-control"
                  required
                />
              </div>

              {/* CC */}
              <div className="col-md-4">
                <label className="form-label fw-semibold">CC</label>
                <input
                  type="text"
                  name="cc"
                  value={formData.cc}
                  onChange={handleChange}
                  className="form-control"
                />
              </div>

              {/* Remarks */}
              <div className="col-md-4">
                <label className="form-label fw-semibold">Remarks</label>
                <textarea
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                  className="form-control"
                  rows="3"
                />
              </div>
            </div>

            <div className="text-center mt-4">
              <button className="btn btn-primary" disabled={loading}>
                {loading ? "Saving..." : "Submit"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* LIST */}
      <div className="card shadow-sm">
        <div className="card-header fw-semibold">
          Saved Out Call Automations
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-hover mb-0">
            <thead className="table-light">
              <tr className="text-center">
                <th>#</th>
                <th>Client</th>
                <th>Campaign</th>
                <th>To</th>
                <th>CC</th>
                <th>Remarks</th>
                <th>Created</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {records.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center">
                    No records found
                  </td>
                </tr>
              ) : (
                records.map((r, i) => (
                  <tr key={r.id} className="text-center">
                    <td>{i + 1}</td>
                    <td>{r.client_name}</td>
                    <td>{r.campaign_name || "-"}</td>
                    <td>{r.to || "-"}</td>
                    <td>{r.cc || "-"}</td>
                    <td>{r.remarks || "-"}</td>
                    <td>{new Date(r.created_at).toLocaleString("en-IN")}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(r.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
